import { useState } from 'react'
import { Plus, Edit, Trash2, Newspaper, Save, X, Upload, Loader2 } from 'lucide-react'
import { useArticleCategories, useCreateArticleCategory, useUpdateArticleCategory, useDeleteArticleCategory, type ArticleCategory } from '@/modules/article-categories-api'
import { uploadImage } from '@/modules/uploads-api'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { useToast } from '../../components/Toast'

function slugify(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60)
}

export function ArticleCategoriesPage() {
  const { data: cats = [], isLoading } = useArticleCategories()
  const createMut = useCreateArticleCategory()
  const updateMut = useUpdateArticleCategory()
  const deleteMut = useDeleteArticleCategory()
  const push = useToast((s) => s.push)

  const [form, setForm] = useState<{ open: boolean; editing: ArticleCategory | null }>({ open: false, editing: null })
  const [v, setV] = useState({ slug: '', name: '', image: '', order: 10 })
  const [uploading, setUploading] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [slugTouched, setSlugTouched] = useState(false)

  const openCreate = () => { setV({ slug: '', name: '', image: '', order: (cats.at(-1)?.order ?? 0) + 10 }); setSlugTouched(false); setForm({ open: true, editing: null }) }
  const openEdit = (c: ArticleCategory) => { setV({ slug: c.slug, name: c.name, image: c.image || '', order: c.order }); setSlugTouched(true); setForm({ open: true, editing: c }) }

  const doUpload = async (file?: File | null) => {
    if (!file) return
    setUploading(true)
    try {
      const url = await uploadImage(file)
      setV((s) => ({ ...s, image: url }))
    } catch (err: any) {
      push('error', err?.message ?? 'Upload thất bại')
    } finally {
      setUploading(false)
    }
  }

  const onPaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items
    if (!items) return
    for (let i = 0; i < items.length; i++) {
      const it = items[i]
      if (it.kind === 'file' && it.type.startsWith('image/')) {
        e.preventDefault()
        doUpload(it.getAsFile())
        return
      }
    }
  }

  const handleSave = async () => {
    if (!v.name.trim()) { push('error', 'Vui lòng nhập tên'); return }
    if (!v.slug.trim()) { push('error', 'Slug trống'); return }
    try {
      if (form.editing) {
        await updateMut.mutateAsync({ id: form.editing.id, input: v })
        push('success', `Đã cập nhật "${v.name}"`)
      } else {
        await createMut.mutateAsync(v)
        push('success', `Đã tạo "${v.name}"`)
      }
      setForm({ open: false, editing: null })
    } catch (err: any) {
      push('error', err?.response?.data?.message ?? err?.message ?? 'Lưu thất bại')
    }
  }

  const handleDelete = async () => {
    if (!deleteId) return
    try {
      await deleteMut.mutateAsync(deleteId)
      push('success', 'Đã xóa chuyên mục')
    } catch (err: any) {
      push('error', err?.message ?? 'Xóa thất bại')
    }
    setDeleteId(null)
  }

  const target = deleteId ? cats.find((c) => c.id === deleteId) : null

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text">Chuyên mục tin tức</h1>
          <p className="text-sm text-muted mt-1">{cats.length} chuyên mục · xóa sẽ chuyển các bài viết sang "Chưa phân loại"</p>
        </div>
        <button onClick={openCreate} className="h-11 px-5 rounded-md bg-brand-600 hover:bg-brand-700 text-white font-semibold text-sm inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Thêm chuyên mục</button>
      </div>

      <div className="bg-surface border border-border rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-muted text-xs uppercase">
              <tr>
                <th className="text-center px-4 py-3 w-16 font-medium">Thứ tự</th>
                <th className="text-left px-4 py-3 w-24 font-medium">Ảnh bìa</th>
                <th className="text-left px-4 py-3 font-medium">Tên chuyên mục</th>
                <th className="text-left px-4 py-3 font-medium">Slug</th>
                <th className="px-4 py-3 w-28"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={5} className="px-4 py-16 text-center text-muted">Đang tải...</td></tr>}
              {!isLoading && cats.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-16 text-center"><Newspaper className="w-14 h-14 mx-auto text-slate-300 mb-2" /><div className="font-semibold text-text">Chưa có chuyên mục nào</div></td></tr>
              )}
              {cats.map((c) => (
                <tr key={c.id} className="border-t border-border hover:bg-slate-50">
                  <td className="px-4 py-3 text-center text-sm font-mono text-muted">{c.order}</td>
                  <td className="px-4 py-3">
                    {c.image ? (
                      <div className="w-16 h-10 rounded overflow-hidden bg-slate-100 border border-border">
                        <img src={c.image} alt={c.name} className="w-full h-full object-contain" onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')} />
                      </div>
                    ) : <span className="text-xs text-muted italic">chưa có</span>}
                  </td>
                  <td className="px-4 py-3 font-medium text-text"><span className="inline-flex items-center gap-2"><Newspaper className="w-4 h-4 text-brand-600" /> {c.name}</span></td>
                  <td className="px-4 py-3 text-xs text-muted font-mono">{c.slug}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(c)} className="w-8 h-8 rounded-md hover:bg-brand-50 text-brand-600 flex items-center justify-center"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => setDeleteId(c.id)} className="w-8 h-8 rounded-md hover:bg-red-50 text-danger flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {form.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div onClick={() => setForm({ open: false, editing: null })} className="absolute inset-0 bg-black/50" />
          <div className="relative bg-surface rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="h-14 px-5 border-b border-border flex items-center justify-between sticky top-0 bg-surface">
              <h3 className="font-semibold text-text">{form.editing ? 'Sửa chuyên mục' : 'Thêm chuyên mục mới'}</h3>
              <button onClick={() => setForm({ open: false, editing: null })} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-muted"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <label className="block">
                <div className="text-sm font-medium text-text mb-1.5">Tên hiển thị <span className="text-danger">*</span></div>
                <input value={v.name} onChange={(e) => { const name = e.target.value; setV((s) => ({ ...s, name, slug: slugTouched ? s.slug : slugify(name) })) }} placeholder="VD: Cẩm nang sử dụng" className="w-full h-11 rounded-md border border-border px-3.5 text-sm outline-none focus:border-brand-500" />
              </label>
              <label className="block">
                <div className="text-sm font-medium text-text mb-1.5">Slug <span className="text-danger">*</span></div>
                <input value={v.slug} onChange={(e) => { setV({ ...v, slug: e.target.value }); setSlugTouched(true) }} placeholder="cam-nang" className="w-full h-11 rounded-md border border-border px-3.5 text-sm font-mono outline-none focus:border-brand-500" />
              </label>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-sm font-medium text-text">Ảnh bìa (URL)</div>
                  <label className="h-8 px-3 rounded-md border border-border bg-surface hover:bg-brand-50 hover:border-brand-500 cursor-pointer inline-flex items-center gap-1.5 text-xs font-medium">
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    Upload
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} />
                  </label>
                </div>
                <input value={v.image} onChange={(e) => setV({ ...v, image: e.target.value })} onPaste={onPaste} placeholder="https://... hoặc Ctrl+V ảnh" className="w-full h-11 rounded-md border border-border px-3.5 text-sm outline-none focus:border-brand-500" />
                {v.image ? (
                  <div tabIndex={0} onPaste={onPaste} className="mt-3 aspect-[16/6] rounded-lg bg-slate-100 border-2 border-dashed border-border overflow-hidden relative group cursor-pointer focus:outline-none focus:border-brand-500">
                    <img src={v.image} alt="preview" className="w-full h-full object-contain" onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')} />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white text-sm font-semibold">Click → Ctrl+V để đổi ảnh</div>
                  </div>
                ) : (
                  <div tabIndex={0} onPaste={onPaste} className="mt-3 aspect-[16/6] rounded-lg bg-brand-50/50 border-2 border-dashed border-brand-300 flex flex-col items-center justify-center text-brand-600 cursor-pointer hover:bg-brand-50 focus:outline-none focus:ring-2 focus:ring-brand-500 transition">
                    {uploading ? <Loader2 className="w-10 h-10 animate-spin" /> : (<><Upload className="w-10 h-10 mb-2" /><div className="font-semibold text-sm">Click vào đây rồi Ctrl+V</div><div className="text-xs text-muted mt-1">Hoặc bấm nút Upload ở trên</div></>)}
                  </div>
                )}
              </div>
              <label className="block">
                <div className="text-sm font-medium text-text mb-1.5">Thứ tự hiển thị</div>
                <input type="number" value={v.order} onChange={(e) => setV({ ...v, order: Number(e.target.value) })} className="w-full h-11 rounded-md border border-border px-3.5 text-sm outline-none focus:border-brand-500" />
              </label>
            </div>
            <div className="px-5 py-4 border-t border-border flex items-center justify-end gap-3 sticky bottom-0 bg-surface">
              <button onClick={() => setForm({ open: false, editing: null })} className="h-10 px-5 rounded-md border border-border text-text hover:bg-slate-50 font-medium text-sm">Hủy</button>
              <button onClick={handleSave} disabled={createMut.isPending || updateMut.isPending} className="h-10 px-5 rounded-md bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white font-semibold text-sm inline-flex items-center gap-2"><Save className="w-4 h-4" />{createMut.isPending || updateMut.isPending ? 'Đang lưu...' : 'Lưu'}</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog open={!!deleteId} title="Xóa chuyên mục" message={`Xóa "${target?.name}"? Các bài viết sẽ chuyển sang "Chưa phân loại".`} confirmLabel="Xóa" onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
    </div>
  )
}
